import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, isAfter, isBefore, addMonths, subMonths, isWithinInterval } from 'date-fns';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const getActiveFiltersCount = ({ searchQuery, temporalFilter, dateRangeStart, dateRangeEnd, hasUnreadMessages, hasOpenDispute, needsReview, canBeCancelled, qrRequired, sortByDate, sortByAmount, paymentMethodFilter }) => {
  let count = 0;
  if (searchQuery && String(searchQuery).trim().length > 0) count += 1;
  if (temporalFilter && temporalFilter !== 'all') count += 1;
  if (dateRangeStart || dateRangeEnd) count += 1;
  if (hasUnreadMessages) count += 1;
  if (hasOpenDispute) count += 1;
  if (needsReview) count += 1;
  if (canBeCancelled) count += 1;
  if (qrRequired) count += 1;
  if (sortByDate && sortByDate !== 'newest') count += 1;
  if (sortByAmount && sortByAmount !== 'none') count += 1;
  if (paymentMethodFilter && paymentMethodFilter.length > 0) count += 1;
  return count;
};

const ReservationsAdvancedFilters = ({
  t,
  searchQuery,
  onSearchQueryChange,
  temporalFilter = 'all',
  onTemporalFilterChange,
  dateRangeStart = null,
  dateRangeEnd = null,
  onDateRangeChange,
  onReset,
  autoFocusOnMount = true,
  filtersExpanded: filtersExpandedProp,
  onFiltersExpandedChange,
  defaultFiltersExpanded = false,
  // Nouveaux filtres
  hasUnreadMessages = false,
  onHasUnreadMessagesChange,
  hasOpenDispute = false,
  onHasOpenDisputeChange,
  needsReview = false,
  onNeedsReviewChange,
  canBeCancelled = false,
  onCanBeCancelledChange,
  qrRequired = false,
  onQrRequiredChange,
  // Tri et filtres supplémentaires
  sortByDate = 'newest',
  onSortByDateChange,
  sortByAmount = 'none',
  onSortByAmountChange,
  paymentMethodFilter = [],
  onPaymentMethodFilterChange
}) => {
  const theme = useTheme();
  const { t: mrT, i18n } = useTranslation('myReservations');
  const { user } = useAuth();
  const [internalFiltersExpanded, setInternalFiltersExpanded] = useState(defaultFiltersExpanded);
  const filtersExpanded = typeof filtersExpandedProp === 'boolean' ? filtersExpandedProp : internalFiltersExpanded;

  const userLocale = useMemo(() => {
    const preferred = user?.preferences?.display?.language || user?.preferences?.language || i18n?.language;
    if (preferred) return String(preferred);
    if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
    return undefined;
  }, [user?.preferences?.display?.language, user?.preferences?.language, i18n?.language]);

  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(userLocale, { month: 'long', year: 'numeric' }),
    [userLocale]
  );
  const weekdayFormatter = useMemo(
    () => new Intl.DateTimeFormat(userLocale, { weekday: 'short' }),
    [userLocale]
  );
  const shortDateFormatter = useMemo(
    () => new Intl.DateTimeFormat(userLocale, { day: '2-digit', month: 'short', year: 'numeric' }),
    [userLocale]
  );

  const setFiltersExpanded = useCallback((next) => {
    if (onFiltersExpandedChange) {
      onFiltersExpandedChange(next);
      return;
    }
    setInternalFiltersExpanded(next);
  }, [onFiltersExpandedChange]);

  const handleAccordionChange = useCallback((event, nextExpanded) => {
    // Toggle only when the click originates from the summary.
    const clickedInSummary = !!event?.target?.closest?.('.MuiAccordionSummary-root');
    if (!clickedInSummary) return;
    setFiltersExpanded(nextExpanded);
  }, [setFiltersExpanded]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(dateRangeStart ? new Date(dateRangeStart) : null);
  const [tempEndDate, setTempEndDate] = useState(dateRangeEnd ? new Date(dateRangeEnd) : null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const activeCount = useMemo(
    () => getActiveFiltersCount({ searchQuery, temporalFilter, dateRangeStart, dateRangeEnd, hasUnreadMessages, hasOpenDispute, needsReview, canBeCancelled, qrRequired, sortByDate, sortByAmount, paymentMethodFilter }),
    [searchQuery, temporalFilter, dateRangeStart, dateRangeEnd, hasUnreadMessages, hasOpenDispute, needsReview, canBeCancelled, qrRequired, sortByDate, sortByAmount, paymentMethodFilter]
  );
  const inputRef = useRef(null);
  const didAutoFocusRef = useRef(false);

  useEffect(() => {
    if (!autoFocusOnMount) return;
    if (didAutoFocusRef.current) return;
    didAutoFocusRef.current = true;

    const el = inputRef.current;
    if (!el || typeof el.focus !== 'function') return;

    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }, [autoFocusOnMount]);

  const handleReset = useCallback(() => {
    onSearchQueryChange('');
    if (onTemporalFilterChange) onTemporalFilterChange('all');
    if (onDateRangeChange) onDateRangeChange(null, null);
    if (onHasUnreadMessagesChange) onHasUnreadMessagesChange(false);
    if (onHasOpenDisputeChange) onHasOpenDisputeChange(false);
    if (onNeedsReviewChange) onNeedsReviewChange(false);
    if (onCanBeCancelledChange) onCanBeCancelledChange(false);
    if (onQrRequiredChange) onQrRequiredChange(false);
    if (onSortByDateChange) onSortByDateChange('newest');
    if (onSortByAmountChange) onSortByAmountChange('none');
    if (onPaymentMethodFilterChange) onPaymentMethodFilterChange([]);
    if (onReset) onReset();
  }, [onSearchQueryChange, onTemporalFilterChange, onDateRangeChange, onHasUnreadMessagesChange, onHasOpenDisputeChange, onNeedsReviewChange, onCanBeCancelledChange, onQrRequiredChange, onSortByDateChange, onSortByAmountChange, onPaymentMethodFilterChange, onReset]);

  const handleClearSearch = useCallback(() => {
    onSearchQueryChange('');
  }, [onSearchQueryChange]);

  const handleSearchChange = useCallback((e) => {
    onSearchQueryChange(e.target.value);
  }, [onSearchQueryChange]);

  const handleTemporalFilterChange = useCallback((event, newValue) => {
    if (newValue !== null && onTemporalFilterChange) {
      onTemporalFilterChange(newValue);
    }
  }, [onTemporalFilterChange]);

  const handleCalendarDateClick = (date) => {
    // Logique simple : si aucune date start, définir start; sinon définir end
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Réinitialiser et définir nouvelle start date
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (date < tempStartDate) {
      // Si la date cliquée est avant start, faire swap
      setTempEndDate(tempStartDate);
      setTempStartDate(date);
    } else {
      // Définir end date
      setTempEndDate(date);
    }
  };

  const handleConfirmDateRange = () => {
    if (onDateRangeChange) {
      onDateRangeChange(
        tempStartDate ? format(tempStartDate, 'yyyy-MM-dd') : null,
        tempEndDate ? format(tempEndDate, 'yyyy-MM-dd') : null
      );
    }
    setShowCalendar(false);
  };

  const handleClearDateRange = () => {
    setTempStartDate(null);
    setTempEndDate(null);
  };

  // Générer les jours du calendrier
  const generateCalendarDays = (month) => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const days = [];
    
    let current = start;
    while (current <= end) {
      days.push(new Date(current));
      current = addDays(current, 1);
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays(calendarMonth);
  const isDateInRange = (date) => {
    if (!tempStartDate || !tempEndDate) return false;
    return isWithinInterval(date, { 
      start: tempStartDate < tempEndDate ? tempStartDate : tempEndDate,
      end: tempStartDate > tempEndDate ? tempStartDate : tempEndDate
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Recherche rapide - toujours visible */}
      <Box sx={{ 
        mb: 2, 
        p: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
      }}>
        <TextField
          fullWidth
          size="small"
          inputRef={inputRef}
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t?.('common.search', { defaultValue: 'Rechercher par titre, propriétaire, adresse, ID...' }) ?? 'Rechercher…'}
          sx={{
            '& .MuiInputBase-root': { 
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }
          }}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, color: 'text.secondary' }}>
                <SearchIcon fontSize="small" />
              </Box>
            ),
            endAdornment: searchQuery ? (
              <IconButton
                size="small"
                aria-label={mrT('filters.temporal.clearSearch', { defaultValue: 'Effacer la recherche' })}
                onClick={handleClearSearch}
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null
          }}
        />
      </Box>

      {/* Accordion Filtres avancés */}
      <Accordion 
        expanded={filtersExpanded}
        onChange={handleAccordionChange}
        sx={{
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          borderRadius: 2,
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="filters-content"
          id="filters-header"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12)
            }
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
            <FilterAltIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'primary.main' }}>
                  {mrT('advancedFilters', { defaultValue: 'Filtres avancés' })}
                </Typography>
            
            {/* Compteur de filtres actifs */}
            {activeCount > 0 && (
              <Chip
                label={`${activeCount} ${activeCount === 1 ? 'filtre' : 'filtres'} actif${activeCount > 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ 
                  ml: 'auto',
                  height: 24,
                  '& .MuiChip-label': { fontSize: '0.75rem', px: 1 }
                }}
              />
            )}
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            {/* 1) Statut temporel */}
            <Box
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                p: 2
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <CalendarTodayIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {mrT('filters.temporal.title', { defaultValue: 'Statut temporel' })}
                </Typography>
              </Stack>
              <ToggleButtonGroup
                value={temporalFilter}
                exclusive
                onChange={handleTemporalFilterChange}
                size="small"
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderRadius: 1.5,
                    py: 0.8,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="all">{mrT('filters.temporal.all', { defaultValue: 'Toutes' })}</ToggleButton>
                <ToggleButton value="upcoming">{mrT('filters.temporal.upcoming', { defaultValue: 'À venir' })}</ToggleButton>
                <ToggleButton value="active">{mrT('filters.temporal.active', { defaultValue: 'En cours' })}</ToggleButton>
                <ToggleButton value="past">{mrT('filters.temporal.past', { defaultValue: 'Passées' })}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* 2) Plage de dates */}
            <Box
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                p: 2
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <CalendarTodayIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {mrT('filters.dateRange', { defaultValue: 'Plage de dates' })}
                </Typography>
              </Stack>

              <Stack spacing={2}>
                {/* Affichage de la plage sélectionnée */}
                {dateRangeStart || dateRangeEnd ? (
                  <Box sx={{
                    p: 1.5,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {dateRangeStart && dateRangeEnd
                            ? `${shortDateFormatter.format(new Date(dateRangeStart))} → ${shortDateFormatter.format(new Date(dateRangeEnd))}`
                            : dateRangeStart
                            ? `${mrT('filters.dateRange.from', { defaultValue: 'À partir du' })} ${shortDateFormatter.format(new Date(dateRangeStart))}`
                            : dateRangeEnd
                            ? `${mrT('filters.dateRange.until', { defaultValue: "Jusqu'au" })} ${shortDateFormatter.format(new Date(dateRangeEnd))}`
                            : ''}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          handleClearDateRange();
                          if (onDateRangeChange) onDateRangeChange(null, null);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        {mrT('filters.temporal.clearSearch', { defaultValue: 'Effacer la recherche' })}
                      </Button>
                    </Stack>
                  </Box>
                ) : null}

                {/* Bouton pour ouvrir le calendrier */}
                {!showCalendar && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setShowCalendar(true)}
                    startIcon={<CalendarTodayIcon />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 1.5,
                      py: 1
                    }}
                  >
                    {dateRangeStart || dateRangeEnd
                      ? mrT('filters.changeDateRange', { defaultValue: 'Modifier la plage' })
                        : mrT('filters.selectDateRange', { defaultValue: 'Sélectionner une plage de dates' })}
                  </Button>
                )}

                {/* Calendrier - Affichage conditionnel */}
                {showCalendar && (
                  <Box sx={{
                    p: 2,
                    backgroundColor: 'background.paper',
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`
                  }}>
                    {/* En-tête du calendrier avec navigation */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <IconButton
                        size="small"
                        onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                        }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {monthFormatter.format(calendarMonth)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                        }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>

                    {/* Jours de la semaine */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                      {Array.from({ length: 7 }).map((_, idx) => {
                        const base = startOfWeek(new Date(), { weekStartsOn: 1 });
                        const d = addDays(base, idx);
                        return (
                          <Box key={idx} sx={{ textAlign: 'center', py: 1 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                              {weekdayFormatter.format(d)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Grille des jours */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 2 }}>
                      {calendarDays.map((day, idx) => {
                        const isCurrentMonth = isSameMonth(day, calendarMonth);
                        const isStart = tempStartDate && isSameDay(day, tempStartDate);
                        const isEnd = tempEndDate && isSameDay(day, tempEndDate);
                        const isInRange = isDateInRange(day);

                        return (
                          <Button
                            key={idx}
                            onClick={() => handleCalendarDateClick(day)}
                            sx={{
                              aspect: '1',
                              p: 0,
                              minWidth: 'auto',
                              height: '100%',
                              fontSize: '0.875rem',
                              fontWeight: isStart || isEnd ? 600 : 400,
                              borderRadius: 1,
                              backgroundColor: isStart || isEnd
                                ? theme.palette.primary.main
                                : isInRange
                                ? alpha(theme.palette.primary.main, 0.2)
                                : 'transparent',
                              color: isStart || isEnd ? 'white' : 'inherit',
                              opacity: isCurrentMonth ? 1 : 0.4,
                              border: isStart || isEnd
                                ? `2px solid ${theme.palette.primary.main}`
                                : '1px solid transparent',
                              '&:hover': {
                                backgroundColor: isCurrentMonth
                                  ? alpha(theme.palette.primary.main, isInRange ? 0.3 : 0.1)
                                  : 'transparent',
                                cursor: isCurrentMonth ? 'pointer' : 'default'
                              },
                              pointerEvents: isCurrentMonth ? 'auto' : 'none'
                            }}
                          >
                            {day.getDate()}
                          </Button>
                        );
                      })}
                    </Box>

                    {/* Instructions */}
                    {(!tempStartDate || !tempEndDate) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, textAlign: 'center' }}>
                        {!tempStartDate
                          ? mrT('filters.calendar.start', { defaultValue: 'Cliquez sur une date pour commencer' })
                          : mrT('filters.calendar.end', { defaultValue: 'Cliquez sur une date pour terminer' })}
                      </Typography>
                    )}

                    {/* Boutons d'action du calendrier */}
                    <Stack direction="row" spacing={1.5}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCalendar(false);
                          setTempStartDate(dateRangeStart ? new Date(dateRangeStart) : null);
                          setTempEndDate(dateRangeEnd ? new Date(dateRangeEnd) : null);
                        }}
                        sx={{ textTransform: 'none', borderRadius: 1 }}
                      >
                        {mrT('filters.calendar.cancel', { defaultValue: 'Annuler' })}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirmDateRange();
                        }}
                        disabled={!tempStartDate && !tempEndDate}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 1
                        }}
                      >
                        {mrT('filters.calendar.confirm', { defaultValue: 'Valider' })}
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* 3) Actions requises */}
            <Box
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.warning.main, 0.02),
                p: 2
              }}
            >
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'warning.main', mb: 1 }}>
                {mrT('actions.required', { defaultValue: 'Actions requises' })}
              </Typography>
              <Stack spacing={1.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasUnreadMessages}
                      onChange={(e) => onHasUnreadMessagesChange && onHasUnreadMessagesChange(e.target.checked)}
                    />
                  }
                  label={mrT('actions.unreadMessages', { defaultValue: 'Messages non lus' })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={qrRequired}
                      onChange={(e) => onQrRequiredChange && onQrRequiredChange(e.target.checked)}
                    />
                  }
                  label={mrT('actions.qrRequired', { defaultValue: 'QR code requis (pickup/return)' })}
                />
              </Stack>
            </Box>

            {/* 4) Statut de réservation */}
            <Box
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.error.main, 0.02),
                p: 2
              }}
            >
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'error.main', mb: 1 }}>
                {mrT('status.title', { defaultValue: 'Statut de réservation' })}
              </Typography>
              <Stack spacing={1.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasOpenDispute}
                      onChange={(e) => onHasOpenDisputeChange && onHasOpenDisputeChange(e.target.checked)}
                    />
                  }
                  label={mrT('status.openDispute', { defaultValue: 'Litige ouvert' })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={needsReview}
                      onChange={(e) => onNeedsReviewChange && onNeedsReviewChange(e.target.checked)}
                    />
                  }
                  label={mrT('status.needsReview', { defaultValue: 'Évaluation à faire' })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={canBeCancelled}
                      onChange={(e) => onCanBeCancelledChange && onCanBeCancelledChange(e.target.checked)}
                    />
                  }
                  label={mrT('status.cancellable', { defaultValue: 'Annulable' })}
                />
              </Stack>
            </Box>

            {/* 5) Tri et filtres supplémentaires */}
            <Box
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.info.main, 0.02),
                p: 2
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'info.main', mb: 1.5 }}>
                {mrT('sortAndFilters.title', { defaultValue: 'Tri et filtres' })}
              </Typography>

              {/* Tri par date de début */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  {mrT('sortAndFilters.startDate', { defaultValue: 'Date de début' })}
                </Typography>
                <ToggleButtonGroup
                  value={sortByDate}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null && onSortByDateChange) {
                      onSortByDateChange(newValue);
                    }
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      borderRadius: 1.5,
                      py: 0.8,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="default">{mrT('sortAndFilters.sortDate.default', { defaultValue: 'Par défaut (création)' })}</ToggleButton>
                  <ToggleButton value="newest">{mrT('sortAndFilters.sortDate.newest', { defaultValue: 'Date de début Imminente' })}</ToggleButton>
                  <ToggleButton value="oldest">{mrT('sortAndFilters.sortDate.oldest', { defaultValue: 'Date de début lointaine' })}</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Tri par montant */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  {mrT('sortAndFilters.amount.label', { defaultValue: 'Montant total' })}
                </Typography>
                <ToggleButtonGroup
                  value={sortByAmount}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null && onSortByAmountChange) {
                      onSortByAmountChange(newValue);
                    }
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      borderRadius: 1.5,
                      py: 0.8,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="none">{mrT('sortAndFilters.amount.default', { defaultValue: 'Défaut' })}</ToggleButton>
                  <ToggleButton value="asc">{mrT('sortAndFilters.amount.asc', { defaultValue: 'Croissant' })}</ToggleButton>
                  <ToggleButton value="desc">{mrT('sortAndFilters.amount.desc', { defaultValue: 'Décroissant' })}</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Filtre mode de paiement */}
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  {mrT('filters.paymentMethod', { defaultValue: 'Mode de paiement' })}
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paymentMethodFilter.includes('cash')}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? [...paymentMethodFilter, 'cash']
                            : paymentMethodFilter.filter(m => m !== 'cash');
                          onPaymentMethodFilterChange && onPaymentMethodFilterChange(newValue);
                        }}
                      />
                    }
                    label={mrT('payment.cash', { defaultValue: 'Cash' })}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paymentMethodFilter.includes('stripe')}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? [...paymentMethodFilter, 'stripe']
                            : paymentMethodFilter.filter(m => m !== 'stripe');
                          onPaymentMethodFilterChange && onPaymentMethodFilterChange(newValue);
                        }}
                      />
                    }
                    label={mrT('payment.card', { defaultValue: 'Carte bancaire' })}
                  />
                </Stack>
              </Box>
            </Box>

            <Divider />

            {/* Bouton Réinitialiser */}
            {activeCount > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleReset}
                fullWidth
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {mrT('filters.reset', { defaultValue: 'Réinitialiser les filtres' })}
              </Button>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default React.memo(ReservationsAdvancedFilters);
